import React, { useCallback, useEffect, useState } from 'react';
import { Card, Descriptions, Table, Tag, Button, Space, Tooltip, message, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, Link } from 'react-router-dom';
import { stockAdjustmentService } from '@services/operator/stockAdjustmentService';
import { CheckOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import useCan from '@shared/permissions/useCan';
import ListPageHeader from '@shared/ListPageHeader';
import InventoryConflictSection from '@shared/inventory/InventoryConflictSection';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

export default function StockAdjustmentShow() {
  const adjustment = useLoaderData();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const can = useCan();
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // 對齊後端 permissions.yml：approve/reject 需 inventories:approve、update(edit) 需 inventories:write。
  // 兩權限獨立 gate，避免純審核者(approve 無 write) 看不到核准鈕、或純編輯者(write 無 approve) 按到 BE 403。
  const canWrite = can('inventories:write');
  const canApprove = can('inventories:approve');
  const hasConflicts = conflicts.length > 0;

  // 自動 preview：頁面進入 pending 狀態時即打一次，section 立即反映目前衝突
  const fetchPreview = useCallback(async () => {
    if (!adjustment?.id || adjustment.status !== 'pending') return;
    setPreviewLoading(true);
    try {
      const response = await stockAdjustmentService.previewApprove(adjustment.id);
      if (response.success) {
        setConflicts(response.data?.conflicts ?? []);
      } else {
        handleApiError(response, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setPreviewLoading(false);
    }
  }, [adjustment?.id, adjustment?.status, t]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleApprove = async () => {
    setLoading(true);
    try {
      const response = await stockAdjustmentService.approve(adjustment.id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        navigate(0);
        return;
      }
      // Race defense：BE 在最後關頭發現可調整量不足（其他人剛剛分配/凍結）
      // → 提示使用者並重新 preview，讓最新衝突重新計算
      if (response.message === 'adjustment_exceeds_available') {
        message.warning(t('shared.inventory_conflict.race_refreshed_message'));
        await fetchPreview();
        return;
      }
      handleApiError(response, { t });
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const response = await stockAdjustmentService.reject(adjustment.id);
      if (response.success) {
        message.success(t('common.message.operation_success'));
        navigate(0);
      } else {
        handleApiError(response, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('models.product.name'),
      key: 'product',
      render: (_, r) => (
        <Link to={`/products/${r.product_id}`}>
          <div>{r.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.product_name}</div>
        </Link>
      )
    },
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
    },
    {
      title: t('models.inventory.inventory_type'),
      key: 'inventory_type',
      render: (_, r) => r.inventory_type_name || '-',
    },
    {
      title: t('models.inventory.batch'),
      key: 'batch',
      render: (_, r) => r.batch || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: v => v || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: v => v || '-',
    },
    {
      title: t('models.stock_adjustment.quantity'),
      key: 'quantity',
      align: 'right',
      render: (_, r) => {
        const isGain = r.quantity > 0;
        return (
          <span style={{ color: isGain ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
            {isGain ? `+${r.quantity}` : r.quantity}
          </span>
        );
      }
    },
    {
      title: t('models.stock_adjustment.reason'),
      key: 'reason_code',
      render: (_, r) => r.reason_code ? t(`enums.stock_adjustment.reason_code.${r.reason_code}`, r.reason_code) : '-'
    },
    {
      title: t('models.stock_adjustment.note'),
      dataIndex: 'note',
      key: 'note',
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'processing';
      default: return 'default';
    }
  };

  // approve button：有衝突時 disable + tooltip 提示；否則維持原本 Popconfirm 確認流程
  const approveButton = (() => {
    const baseButton = (
      <Button
        type="primary"
        icon={<CheckOutlined />}
        loading={loading}
        disabled={hasConflicts || previewLoading}
      >
        {t('common.button.approve')}
      </Button>
    );

    if (hasConflicts) {
      return (
        <Tooltip title={t('shared.inventory_conflict.approve_blocked_tooltip', { count: conflicts.length })}>
          {baseButton}
        </Tooltip>
      );
    }

    return (
      <Popconfirm
        title={t('shared.stock_adjustment.approve_confirm_title')}
        description={t('shared.stock_adjustment.approve_confirm_content', { name: adjustment.name })}
        onConfirm={handleApprove}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
      >
        {baseButton}
      </Popconfirm>
    );
  })();

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <ListPageHeader
        items={['stocks', 'stocks.adjustments', { label: adjustment.name }]}
        title={
          <Space>
            {adjustment.name}
            <Tag color={getStatusColor(adjustment.status)}>{t(`enums.stock_adjustment.status.${adjustment.status}`)}</Tag>
          </Space>
        }
        actions={
          adjustment.status === 'pending' && (canWrite || canApprove) ? (
            <Space>
              {canWrite && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/stock_adjustments/${adjustment.id}/edit`)}
                >
                  {t('common.button.edit')}
                </Button>
              )}
              {canApprove && (
                <Popconfirm
                  title={t('shared.stock_adjustment.reject_confirm_title')}
                  description={t('shared.stock_adjustment.reject_confirm_content', { name: adjustment.name })}
                  onConfirm={handleReject}
                  okText={t('common.button.confirm')}
                  cancelText={t('common.button.cancel')}
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<CloseOutlined />} loading={loading}>
                    {t('common.button.reject')}
                  </Button>
                </Popconfirm>
              )}
              {canApprove && approveButton}
            </Space>
          ) : undefined
        }
      />

      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Descriptions title={t('shared.stock_adjustment.basic_info')} bordered>
          <Descriptions.Item label={t('models.stock_adjustment.created_at')}>{adjustment.created_at}</Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.applicant')}>{adjustment.applicant?.name}</Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.approver')}>{adjustment.approver?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('models.stock_adjustment.note')} span={3}>{adjustment.note || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {adjustment.status === 'pending' && (
        <InventoryConflictSection
          conflicts={conflicts}
          loading={previewLoading}
          onRefresh={fetchPreview}
        />
      )}

      <Card title={t('shared.stock_adjustment.items')} bordered={false}>
        <Table
          columns={columns}
          dataSource={adjustment.items}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
}
